import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Send,
  CircleCheck,
  Coins,
  Loader2,
  RotateCcw,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatUsd,
  REBATE_STATUS_LABEL,
  REBATE_STATUS_TONE,
  REBATE_NEXT_STATUS,
  REBATE_PREV_STATUS,
  advanceRebate,
  undoRebate,
  STORES,
  STORE_REBATES,
  QUARTER_OPTIONS,
  summarizeBatch,
  setRebateStatusBatch,
  makeRebateBatchId,
  dayToIso,
} from '@/lib/rebatesData';
import { toast } from 'sonner';
import RebateBatchDialog from '@/components/rebates/RebateBatchDialog';

const fmtDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const batchLabel = (b) =>
  `${fmtDay(b.sent_at)} · ${b.stores.join(', ')} · ${b.count} · ${formatUsd(b.total)}`;

// Which stage each batch action starts from.
const BATCH_FROM = { send: 'logged', receive: 'submitted', undo: 'submitted' };
const BATCH_TARGET = { send: 'submitted', receive: 'received', undo: 'logged' };

const STORE_BADGE = {
  Menards: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  Lowes: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  'Home Depot': 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  Other: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

const STATUS_BADGE = {
  default: 'bg-[hsl(var(--muted))] text-foreground border-border',
  warning:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  success:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
};

const Row = ({ row, onChange, busyKey, setBusyKey, jobs }) => {
  const { t } = useTranslation();
  const tone = REBATE_STATUS_TONE[row.rebate_status] || 'default';
  // Status label key mapping: rebatesData.REBATE_STATUS_LABEL stays English-only
  // for CSV exports and back-compat; the UI uses translated labels.
  const statusLabel = t(`rebateTracker.status.${row.rebate_status}`, {
    defaultValue: REBATE_STATUS_LABEL[row.rebate_status] || row.rebate_status,
  });
  const isBusy = busyKey === row.row_key;
  const canAdvance = !!REBATE_NEXT_STATUS[row.rebate_status];
  const canUndo = !!REBATE_PREV_STATUS[row.rebate_status];

  // Map the current status to the localized "next-step" button label.
  // logged    -> "Mark Submitted"
  // submitted -> "Mark Received"
  // received  -> no button (terminal)
  const advanceLabel = canAdvance
    ? t(`rebateTracker.table.advanceLabel.${row.rebate_status}`, {
        defaultValue:
          row.rebate_status === 'logged' ? 'Mark Submitted' : 'Mark Received',
      })
    : '';

  const runAction = async (fn) => {
    const job = jobs.find((j) => j.id === row.job_id);
    if (!job) return;
    setBusyKey?.(row.row_key);
    try {
      const updated = await fn(job, row.receipt_id);
      toast.success(
        t('rebateTracker.table.markedToast', {
          status: t(`rebateTracker.status.${updated?.receipts?.find?.((r) => r?.id === row.receipt_id)?.rebate_status || ''}`, {
            defaultValue:
              REBATE_STATUS_LABEL[
                updated?.receipts?.find?.((r) => r?.id === row.receipt_id)?.rebate_status
              ] || '',
          }),
        })
      );
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.message || t('rebateTracker.table.updateFailed'));
    } finally {
      setBusyKey?.(null);
    }
  };

  const handleAdvance = () => runAction(advanceRebate);
  const handleUndo = () => runAction(undoRebate);

  // Pick the right icon for the advance button based on the *next* stage.
  const AdvanceIcon =
    REBATE_NEXT_STATUS[row.rebate_status] === 'received' ? CircleCheck : Send;

  return (
    <div
      className="grid grid-cols-12 gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors"
      data-testid={`rebate-line-${row.row_key}`}
    >
      <div className="col-span-12 sm:col-span-3 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {row.job_name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {row.client || t('rebateTracker.table.noClient')} {row.desc ? <span> {'\u00b7'} {row.desc}</span> : null}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Badge
          variant="outline"
          className={cn('uppercase text-[10px] tracking-[0.16em]', STORE_BADGE[row.store] || STORE_BADGE.Other)}
        >
          {row.store}
        </Badge>
        <div className="text-[11px] text-muted-foreground mt-1">
          {row.date || t('rebateTracker.table.unknown')} {'\u00b7'} {row.quarter}
        </div>
      </div>
      <div className="col-span-3 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('rebateTracker.table.spent')}
        </div>
        <div className="text-sm text-foreground tabular-nums">{formatUsd(row.amount)}</div>
      </div>
      <div className="col-span-3 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('rebateTracker.table.rebate')}
        </div>
        <div className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums">
          {formatUsd(row.rebate)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {Math.round(row.rebate_rate * 100)}% {t('rebateTracker.table.rateSuffix')}
        </div>
      </div>
      <div className="col-span-3 sm:col-span-2">
        <Badge
          variant="outline"
          className={cn('uppercase text-[10px] tracking-[0.16em]', STATUS_BADGE[tone])}
          data-testid={`rebate-line-status-${row.row_key}`}
        >
          {statusLabel}
        </Badge>
      </div>
      <div className="col-span-9 sm:col-span-2 flex items-center justify-end gap-1.5 flex-wrap">
        {/* Contextual single next-step button.
            logged    -> Mark Submitted
            submitted -> Mark Received
            received  -> (none — terminal state) */}
        {canAdvance ? (
          <Button
            size="sm"
            onClick={handleAdvance}
            disabled={isBusy || !!busyKey}
            className={cn(
              'h-8 uppercase tracking-[0.16em] text-[10px] border',
              REBATE_NEXT_STATUS[row.rebate_status] === 'received'
                ? 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30'
                : 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/25'
            )}
            data-testid={`rebate-advance-${row.row_key}`}
          >
            {isBusy ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <AdvanceIcon size={11} className="mr-1.5" />
            )}
            {advanceLabel}
          </Button>
        ) : null}
        {/* Undo — only when the receipt has progressed past `logged`.
            Steps back exactly one stage. */}
        {canUndo ? (
          <Button
            size="sm"
            variant="outline"
            onClick={handleUndo}
            disabled={isBusy || !!busyKey}
            className="h-8 border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-[10px]"
            data-testid={`rebate-undo-${row.row_key}`}
            aria-label={t('rebateTracker.table.undoAria')}
            title={t('rebateTracker.table.undoTooltip')}
          >
            {isBusy && !canAdvance ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RotateCcw size={11} className="mr-1.5" />
            )}
            {t('rebateTracker.table.undo')}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const ListSkeleton = () => (
  <div className="divide-y divide-border" data-testid="rebates-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-6 w-24 ml-auto" />
      </div>
    ))}
  </div>
);

export const RebatesTable = ({
  rows,
  loading,
  jobs,
  storeFilter,
  onStoreFilter,
  statusFilter,
  onStatusFilter,
  quarterFilter,
  onQuarterFilter,
  search,
  onSearch,
  onChanged,
  batches = [],
  batchFilter = 'all',
  onBatchFilter,
  onBatchSent,
}) => {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState(null);
  const [batchMode, setBatchMode] = useState(null); // 'send' | 'receive' | 'undo'
  const [batchBusy, setBatchBusy] = useState(false);

  const handleChanged = (updated) => {
    onChanged?.(updated);
  };

  // A batch acts on exactly what's on screen — filter to Menards first and
  // only the Menards receipts go in the batch.
  const batchSummary = useMemo(
    () => (batchMode ? summarizeBatch(rows, BATCH_FROM[batchMode]) : null),
    [batchMode, rows]
  );
  const readySummary = useMemo(() => summarizeBatch(rows, 'logged'), [rows]);
  const waitingSummary = useMemo(() => summarizeBatch(rows, 'submitted'), [rows]);

  const runBatch = async (day) => {
    if (!batchSummary?.count) return;
    const mode = batchMode;
    const at = day ? dayToIso(day) : null;
    const batchId = mode === 'send' ? makeRebateBatchId(day ? new Date(`${day}T12:00:00`) : new Date()) : null;
    setBatchBusy(true);
    try {
      const res = await setRebateStatusBatch(batchSummary.rows, BATCH_TARGET[mode], { at, batchId });
      res.jobs.forEach((j) => onChanged?.(j));
      if (res.moved) {
        toast.success(
          t(`rebateTracker.batch.${mode}Done`, {
            count: res.moved,
            total: formatUsd(batchSummary.total),
          })
        );
      }
      if (res.skipped) {
        toast.info(t('rebateTracker.batch.skipped', { count: res.skipped }));
      }
      if (res.failed.length) {
        toast.error(t('rebateTracker.batch.failed', { count: res.failed.length }));
      }
      setBatchMode(null);
      if (mode === 'send' && res.moved) onBatchSent?.(batchId);
      if (mode === 'undo') onBatchFilter?.('all');
    } catch (e) {
      toast.error(e?.message || t('rebateTracker.table.updateFailed'));
    } finally {
      setBatchBusy(false);
    }
  };

  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="rebates-table-card"
    >
      <CardContent className="p-3 sm:p-4">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3">
          <Tabs value={statusFilter} onValueChange={onStatusFilter} className="w-full sm:w-auto">
            <TabsList
              className="bg-[hsl(var(--muted))] border border-border w-full sm:w-auto overflow-x-auto no-scrollbar"
              data-testid="rebates-status-tabs"
            >
              <TabsTrigger value="all" className="text-xs uppercase tracking-[0.16em]">
                {t('rebateTracker.table.tabs.all')}
              </TabsTrigger>
              <TabsTrigger value="logged" className="text-xs uppercase tracking-[0.16em]" data-testid="rebates-tab-logged">
                {t('rebateTracker.table.tabs.logged')}
              </TabsTrigger>
              <TabsTrigger value="submitted" className="text-xs uppercase tracking-[0.16em]" data-testid="rebates-tab-submitted">
                {t('rebateTracker.table.tabs.submitted')}
              </TabsTrigger>
              <TabsTrigger value="received" className="text-xs uppercase tracking-[0.16em]" data-testid="rebates-tab-received">
                {t('rebateTracker.table.tabs.received')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={storeFilter} onValueChange={onStoreFilter}>
            <SelectTrigger
              className="h-9 w-full sm:w-[160px] bg-[hsl(var(--muted))] border-border"
              data-testid="rebates-store-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-border">
              <SelectItem value="all">{t('rebateTracker.table.allStores')}</SelectItem>
              {/* Only stores that earn a rebate. Lowe's and Home Depot default
                  to 0% and were listed for nothing (John, 2026-10-13). */}
              {STORES.filter((s) => s !== 'Other' && Number(STORE_REBATES[s]) > 0).map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={quarterFilter} onValueChange={onQuarterFilter}>
            <SelectTrigger
              className="h-9 w-full sm:w-[160px] bg-[hsl(var(--muted))] border-border"
              data-testid="rebates-quarter-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-border">
              {QUARTER_OPTIONS.map((q) => (
                <SelectItem key={q.value} value={q.value}>
                  {q.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('rebateTracker.table.searchPlaceholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="rebates-search"
              />
            </div>
          </div>
        </div>

        {/* Batch actions — mailed a stack, or the money came back. */}
        {statusFilter === 'logged' ? (
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 px-3 py-2.5 mb-3"
            data-testid="rebates-batch-bar"
          >
            <div className="text-sm text-foreground">
              {readySummary.count
                ? t('rebateTracker.batch.readyLine', {
                    count: readySummary.count,
                    total: formatUsd(readySummary.total),
                  })
                : t('rebateTracker.batch.nothingReady')}
            </div>
            <Button
              size="sm"
              onClick={() => setBatchMode('send')}
              disabled={!readySummary.count || !!busyKey}
              className="h-9 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-[11px]"
              data-testid="rebates-batch-send"
            >
              <Send size={12} className="mr-1.5" />
              {t('rebateTracker.batch.sendButton')}
            </Button>
          </div>
        ) : null}
        {statusFilter === 'submitted' ? (
          <div
            className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2.5 mb-3"
            data-testid="rebates-batch-bar"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
              <Select value={batchFilter} onValueChange={onBatchFilter}>
                <SelectTrigger
                  className="h-9 w-full sm:w-[300px] bg-[hsl(var(--card))] border-border text-xs"
                  data-testid="rebates-batch-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="all">{t('rebateTracker.batch.allBatches')}</SelectItem>
                  {batches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {batchLabel(b)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {t('rebateTracker.batch.waitingLine', {
                  count: waitingSummary.count,
                  total: formatUsd(waitingSummary.total),
                })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {batchFilter !== 'all' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBatchMode('undo')}
                  disabled={!waitingSummary.count || !!busyKey}
                  className="h-9 text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-[11px]"
                  data-testid="rebates-batch-undo"
                >
                  <Undo2 size={12} className="mr-1.5" />
                  {t('rebateTracker.batch.undoButton')}
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={() => setBatchMode('receive')}
                disabled={!waitingSummary.count || !!busyKey}
                className="h-9 bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30 uppercase tracking-[0.16em] text-[11px]"
                data-testid="rebates-batch-receive"
              >
                <CircleCheck size={12} className="mr-1.5" />
                {t('rebateTracker.batch.receiveButton')}
              </Button>
            </div>
          </div>
        ) : null}

        {/* Table header */}
        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="col-span-3">{t('rebateTracker.table.colJobClient')}</span>
          <span className="col-span-2">{t('rebateTracker.table.colStore')}</span>
          <span className="col-span-1">{t('rebateTracker.table.colSpent')}</span>
          <span className="col-span-2">{t('rebateTracker.table.colRebate')}</span>
          <span className="col-span-2">{t('rebateTracker.table.colStatus')}</span>
          <span className="col-span-2 text-right">{t('rebateTracker.table.colActions')}</span>
        </div>

        {loading ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="rebates-empty"
          >
            <Coins size={20} className="text-muted-foreground/70" />
            {t('rebateTracker.table.empty')}
          </div>
        ) : (
          <div data-testid="rebates-list">
            {rows.map((row) => (
              <Row
                key={row.row_key}
                row={row}
                jobs={jobs}
                busyKey={busyKey}
                setBusyKey={setBusyKey}
                onChange={handleChanged}
              />
            ))}
          </div>
        )}
        <RebateBatchDialog
          open={!!batchMode}
          onOpenChange={(o) => !o && setBatchMode(null)}
          mode={batchMode || 'send'}
          summary={batchSummary}
          busy={batchBusy}
          onConfirm={runBatch}
        />
      </CardContent>
    </Card>
  );
};

export default RebatesTable;
